import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useFinalizeIncidentReport,
  useIncidentReport,
  useIncidentReportPdfUrl,
  useUpdateIncidentReport,
  type IncidentReportV1,
} from "@/api/queries/incidents";
import { useAuth } from "@/auth/AuthProvider";
import { canEditReport } from "@/lib/permissions";
import { safeReportUrl } from "@/lib/reports";

function arrayField(value: string): string[] {
  return value
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
}

type Props = { incidentId: string };

export function ReportEditor({ incidentId }: Props) {
  const { identity } = useAuth();
  const editable = canEditReport(identity?.role);
  const { data, isLoading } = useIncidentReport(incidentId);
  const update = useUpdateIncidentReport(incidentId);
  const finalize = useFinalizeIncidentReport(incidentId);
  const pdf = useIncidentReportPdfUrl(incidentId);

  const [hazards, setHazards] = useState("");
  const [decon, setDecon] = useState("");
  const [gaps, setGaps] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!data) return;
    setHazards(data.body.hazards_identified.join("\n"));
    setDecon(data.body.decon_actions.join("\n"));
    setGaps(data.body.gaps.join("\n"));
    setNotes(data.body.after_action_notes);
  }, [data]);

  if (isLoading) return <Skeleton className="h-64" />;
  if (!data) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-muted-foreground">
          No report draft yet. It's generated when the incident closes.
        </CardContent>
      </Card>
    );
  }

  const locked = data.status === "finalized" || !editable;

  const onSave = () => {
    const next: IncidentReportV1 = {
      ...data.body,
      hazards_identified: arrayField(hazards),
      decon_actions: arrayField(decon),
      gaps: arrayField(gaps),
      after_action_notes: notes,
    };
    update.mutate(next);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Report draft: {data.status}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 lg:grid-cols-2">
          <FieldList
            label="Hazards identified"
            value={hazards}
            onChange={setHazards}
            disabled={locked}
          />
          <FieldList
            label="Decon actions"
            value={decon}
            onChange={setDecon}
            disabled={locked}
          />
          <FieldList
            label="Gaps"
            value={gaps}
            onChange={setGaps}
            disabled={locked}
          />
          <div className="space-y-1">
            <label className="text-sm font-medium">After-action notes</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={locked}
              rows={6}
            />
          </div>
        </div>

        {data.markdown ? (
          <details className="rounded-md border border-border p-3 text-sm">
            <summary className="cursor-pointer text-muted-foreground">
              Preview markdown
            </summary>
            <pre className="mt-2 whitespace-pre-wrap text-xs">
              {data.markdown}
            </pre>
          </details>
        ) : null}

        <div className="flex items-center gap-2">
          <Button
            data-testid="save-report"
            onClick={onSave}
            disabled={locked || update.isPending}
          >
            {update.isPending ? "Saving…" : "Save draft"}
          </Button>
          <Button
            variant="destructive"
            data-testid="finalize-report"
            disabled={locked || finalize.isPending || data.status !== "ready"}
            onClick={() => finalize.mutate()}
          >
            {finalize.isPending ? "Finalizing…" : "Finalize"}
          </Button>
          {data.pdf_s3_key ? (
            <Button
              variant="outline"
              data-testid="download-report-pdf"
              disabled={pdf.isPending}
              onClick={() =>
                pdf.mutate(
                  { download: true },
                  {
                    onSuccess: (res) => {
                      // The API presigned this URL with `Content-Disposition:
                      // attachment`, so navigating to it makes the browser save
                      // the PDF instead of rendering it inline in a new tab. No
                      // blob fetch (which the reports bucket's CORS would block)
                      // and no `download` attribute (ignored cross-origin) — the
                      // server-side header does the work.
                      const safeUrl = safeReportUrl(res.url);
                      if (!safeUrl) return;
                      const a = document.createElement("a");
                      a.href = safeUrl;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                    },
                  },
                )
              }
            >
              {pdf.isPending ? "Preparing…" : "Download PDF"}
            </Button>
          ) : null}
          {data.status === "finalized" ? (
            <span className="text-xs text-muted-foreground">
              Locked: finalized.
            </span>
          ) : null}
        </div>
        {pdf.isError ? (
          <p className="text-xs text-destructive">
            {pdf.error instanceof Error
              ? pdf.error.message
              : "Could not download the report PDF."}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

function FieldList({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium">{label}</label>
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        rows={5}
        placeholder="One item per line"
      />
    </div>
  );
}
